import { auth } from '../firebase/config';

class KeypointPredictionService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';
    
    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.apiUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8000'  // Local Container Apps development
      : `https://keypoint-detector-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Extract frame data from video element as base64 for API transmission
   */
  extractFrameAsBase64(sourceElement) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas to original size — works for both <video> and <canvas> sources
      const w = sourceElement.videoWidth || sourceElement.width;
      const h = sourceElement.videoHeight || sourceElement.height;
      canvas.width = w;
      canvas.height = h;
      
      // Draw current frame
      ctx.drawImage(sourceElement, 0, 0);
      
      // Convert to base64 JPEG (smaller than PNG for transmission)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      // Extract base64 data without the data:image/jpeg;base64, prefix
      const base64Data = dataUrl.split(',')[1];
      
      return base64Data;
    } catch (error) {
      console.error('❌ Frame extraction failed:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      if (error.name === 'SecurityError' || error.message.includes('insecure') || error.message.includes('CORS')) {
        throw new Error('Cannot extract video frame data due to CORS restrictions. Please ensure the video has proper CORS headers or is served from the same origin.');
      }
      throw error;
    }
  }

  /**
   * Predict court keypoints from video element using server-side API
   */
  async predict(sourceElement) {
    try {
      // Store original dimensions (works for both <video> and <canvas>)
      const originalWidth = sourceElement.videoWidth || sourceElement.width;
      const originalHeight = sourceElement.videoHeight || sourceElement.height;
      
      // Extract frame as base64
      const imageData = this.extractFrameAsBase64(sourceElement);

      // Get Firebase ID token
      const user = auth.currentUser;
      const idToken = user ? await user.getIdToken() : null;

      const headers = { 'Content-Type': 'application/json' };
      if (idToken) {
        headers['X-Firebase-Token'] = idToken;
      }
      
      // Call the API
      const apiEndpoint = `${this.apiUrl}/api/predict`;
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          image_data: imageData,
          width: originalWidth,
          height: originalHeight
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} - ${errorText.substring(0, 200)}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Prediction failed');
      }

      return result.keypoints;
      
    } catch (error) {
      console.error('Keypoint prediction failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const keypointPredictionService = new KeypointPredictionService();
